import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStrava } from '../contexts/StravaContext';

const { FiActivity, FiTrendingUp, FiZap, FiArrowRight, FiImage, FiPalette } = FiIcons;

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, authenticateWithStrava, enableDemoMode, handleAuthCallback } = useStrava();

  // Check if there's an error in the URL (e.g. after a failed authentication)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    if (error) {
      console.error('Strava authentication error:', error);
    }

    // Handle Strava OAuth callback on the homepage
    if (code && !error) {
      console.log('Detected auth code in URL, processing...');
      const processAuth = async () => {
        try {
          await handleAuthCallback(code);
          navigate('/dashboard');
        } catch (err) {
          console.error('Failed to process authentication:', err);
        }
      };
      processAuth();
    }
  }, [location, handleAuthCallback, navigate]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      authenticateWithStrava();
    }
  };

  const handleDemoMode = () => {
    enableDemoMode();
    navigate('/dashboard');
  };

  const handleViewAnalysis = () => {
    navigate('/analysis');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        className="max-w-4xl w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Side - Text Content */}
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white">TempoPainter</h1>
              </div>

              <h2 className="text-4xl font-bold text-white mb-6">
                Transform your runs into{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-500">
                  stunning artwork
                </span>
              </h2>

              <p className="text-gray-300 mb-8">
                Connect your Strava account and turn your running data into beautiful visualizations and AI-generated art. Analyze your tempo, pace, and elevation in a completely new way.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.button
                  onClick={handleGetStarted}
                  className="flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Connect with Strava
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </motion.button>

                <motion.button
                  onClick={handleDemoMode}
                  className="flex items-center justify-center gap-2 px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Try Demo Mode
                </motion.button>

                <motion.button
                  onClick={handleViewAnalysis}
                  className="flex items-center justify-center gap-2 px-8 py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 font-medium rounded-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiPalette} className="w-5 h-5" />
                  View Sample
                </motion.button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-orange-400 mt-1" />
                  <div>
                    <h3 className="text-white font-medium">Tempo Analysis</h3>
                    <p className="text-gray-400 text-sm">Visualize your pace and effort</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiImage} className="w-5 h-5 text-pink-400 mt-1" />
                  <div>
                    <h3 className="text-white font-medium">AI Artwork</h3>
                    <p className="text-gray-400 text-sm">Generate unique art from your runs</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiZap} className="w-5 h-5 text-yellow-400 mt-1" />
                  <div>
                    <h3 className="text-white font-medium">Performance Insights</h3>
                    <p className="text-gray-400 text-sm">Track progress over time</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiActivity} className="w-5 h-5 text-green-400 mt-1" />
                  <div>
                    <h3 className="text-white font-medium">Activity Tracking</h3>
                    <p className="text-gray-400 text-sm">Sync with your Strava data</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Side - Image/Visualization */}
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop"
                alt="Running Visualization"
                className="w-full h-auto rounded-xl mb-4"
              />
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-white font-medium">Morning Run</h3>
                  <p className="text-gray-400 text-xs">8.2km • 42:15 • 5:12/km</p>
                </div>
                <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                  Run
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage;